/**
 * Hooks de TanStack Query para el Asistente Formulador (slice 1).
 *
 * Encapsulan el server-state de formulaciones: fetch por proyecto,
 * creacion, redaccion IA de modulos y guardado de ediciones.
 *
 * Convencion de invalidacion: toda mutacion que cambia el contenido de
 * una formulacion invalida `["formulation-by-project", projectId]` para
 * que el FormulationDocument actualice su estado sin doble fuente de
 * verdad. Se usa el `projectId` (no el formulationId) porque es el
 * identificador que tiene el caller en la mayoria de contextos.
 *
 * En error 402 desde `useDraftModule`, `detailData.reason` se expone en
 * `error.detailData` para que el caller muestre el upsell correcto
 * (tier_locked_pro_required vs ai_quota_exhausted).
 *
 * Slice 2 (ADR-021): los modulos de preparacion pueden devolver 409
 * `{reason: "identificacion_incompleta"}` si el estado local esta
 * desactualizado (ej. un modulo de identificacion se des-confirmo en otra
 * pestaña). `useDraftModule`/`useUpdateModule` invalidan la formulacion en
 * ese caso para que la UI se re-sincronice sola; el caller solo necesita
 * leer `error.status === 409` para mostrar un aviso amable.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/lib/errors";
import { myPlanQueryKey } from "@/lib/hooks/useActiveProjectsQuota";
import {
  createFormulation,
  draftFormulationModule,
  getFormulationByProject,
  updateFormulationModule,
  type FormulationDTO,
} from "@/lib/api/formulations";

// --- Fetch ---

/**
 * Lee la formulacion de un expediente idea-first por su project_id.
 *
 * `enabled` debe ser `project.opportunity_id === null` para no disparar
 * el fetch en expedientes desde convocatoria (no tienen formulacion).
 *
 * `data === null` significa que el expediente no tiene formulacion (el API
 * respondio 404). Es un dato, no un error (issue #572): la pagina lo trata
 * como un expediente sin formulacion. `isError` queda para lo que si es un
 * fallo (5xx, red), que la pagina muestra con un "Reintentar".
 */
export function useFormulationByProject(
  projectId: string | undefined,
  municipality: string | undefined,
  enabled: boolean,
) {
  return useQuery<FormulationDTO | null, ApiError>({
    queryKey: ["formulation-by-project", projectId],
    queryFn: async () => {
      try {
        return await getFormulationByProject(projectId!, municipality!);
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
    enabled: enabled && Boolean(projectId) && Boolean(municipality),
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 401) return false;
      return count < 2;
    },
  });
}

// --- Mutations ---

/**
 * Crea una formulacion idea-first (expediente nuevo + 6 modulos vacios).
 *
 * En exito, el caller navega a `/expedientes/:project_id` con el
 * `formulationId` en `location.state` como hint para scroll inicial.
 *
 * Invalida el plan porque el expediente nuevo consume un cupo de los
 * activos (issue #568): sin esto, el plan cacheado seguiria diciendo que
 * queda espacio hasta que venza su `staleTime` de 5 minutos.
 */
export function useCreateFormulation() {
  const queryClient = useQueryClient();
  return useMutation<FormulationDTO, ApiError, { municipality: string; ideaText: string }>({
    mutationFn: ({ municipality, ideaText }) => createFormulation(municipality, ideaText),
    onSuccess: (_data, { municipality }) => {
      void queryClient.invalidateQueries({ queryKey: myPlanQueryKey(municipality) });
    },
  });
}

/**
 * Pide a la IA que redacte (o ajuste) un modulo.
 *
 * Gated por tier Pro + cuota: en 402 el error expone `detailData.reason`
 * para discriminar el upsell (`tier_locked_pro_required` vs
 * `ai_quota_exhausted`).
 *
 * Invalida `["formulation-by-project", projectId]` en exito para que
 * el FormulationDocument recargue el modulo actualizado.
 */
export function useDraftModule(formulationId: string, municipality: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    { module: import("@/lib/api/formulations").ModuleDTO; quota_remaining: number },
    ApiError,
    { moduleKey: string; userMessage?: string }
  >({
    mutationFn: ({ moduleKey, userMessage }) =>
      draftFormulationModule({ formulationId, moduleKey, municipality, userMessage }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["formulation-by-project", projectId],
      });
    },
    onError: (err) => {
      // Gate de preparacion (ADR-021 regla 5): estado local desactualizado.
      // Refrescamos la formulacion para que la UI re-evalue el bloqueo.
      if (err.status === 409) {
        void queryClient.invalidateQueries({
          queryKey: ["formulation-by-project", projectId],
        });
      }
    },
  });
}

/**
 * Guarda una edicion humana o confirma el contenido de un modulo.
 *
 * Invalida la formulacion del proyecto para mantener la UI sincronizada.
 */
export function useUpdateModule(formulationId: string, municipality: string, projectId: string) {
  const queryClient = useQueryClient();
  return useMutation<
    FormulationDTO,
    ApiError,
    { moduleKey: string; content: string; status: "draft" | "confirmed" }
  >({
    mutationFn: ({ moduleKey, content, status }) =>
      updateFormulationModule({ formulationId, moduleKey, municipality, content, status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["formulation-by-project", projectId],
      });
    },
    onError: (err) => {
      // Mismo gate 409 que useDraftModule — ver comentario arriba.
      if (err.status === 409) {
        void queryClient.invalidateQueries({
          queryKey: ["formulation-by-project", projectId],
        });
      }
    },
  });
}
